import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Title, Text } from 'components';
import { AuthContext } from 'contexts';
import { formatCurrency, hasValidStripeMlsProductId } from 'utils';

import { StyledActivateSection } from '../StyledComponents';
import AppointmentDeskContext from '../AppointmentDeskContext';
import { getFinalPriceWithTier, pricingTierCalculator } from '../util';

const YourTotal: React.FC = () => {
  const [listingCount, setListingCount] = useState<number>(0);

  const {
    association,
    portalConfig,
    subscriptionType,
    enhancedPrices,
    enabledListingsCount,
    globalTaxPercentage,
  } = useContext(AppointmentDeskContext);
  const { auth, onboardingPortalConfig } = useContext(AuthContext);

  useEffect(() => {
    const fetchListingCount = async () => {
      const {
        data: { count },
      } = await axios.get<{ count: number }>(
        `/listing/answering_count/${auth.org.id}`,
      );
      setListingCount(count);
    };
    if (subscriptionType === 'org') fetchListingCount();
  }, [subscriptionType]);

  if (!portalConfig) return null;

  const { agentPrice, brokeragePrice } = portalConfig;

  const taxRate = (globalTaxPercentage || 0) / 100;

  // Agent pricing
  let price = agentPrice / 100;
  let taxes = price * taxRate;
  let total = price + taxes;
  let feeDescription = 'Monthly fee (unlimited listings)';

  // Brokerage pricing
  if (subscriptionType === 'org') {
    price = (brokeragePrice / 100) * listingCount;
    taxes = price * taxRate;
    total = price + taxes;
    feeDescription = `${listingCount} Subscribed Listings at ${formatCurrency(
      brokeragePrice / 100,
    )} / listing`;
  }

  // Enhanced Live Concierge pricing
  if (
    hasValidStripeMlsProductId(portalConfig.mlsProductId) &&
    !association?.useLiveConciergePlus
  ) {
    const pricingTier = pricingTierCalculator(
      enhancedPrices,
      enabledListingsCount,
    );
    price = getFinalPriceWithTier(enhancedPrices, enabledListingsCount);
    taxes = price * taxRate;
    total = price + taxes;
    feeDescription = `${enabledListingsCount} Subscribed Listings at $${pricingTier} / listing`;
  }

  return (
    <StyledTotalSection>
      <StyledTitle level={3} data-cy="first-month-title">
        First Month&apos;s Payment
      </StyledTitle>
      {(subscriptionType === 'org' ||
        hasValidStripeMlsProductId(portalConfig.mlsProductId)) && (
        <StyledSecondaryText>
          <Text type="secondary" data-cy="first-month-sub-title">
            The total subscription amount might change if you update the number
            of active listings{' '}
            {hasValidStripeMlsProductId(portalConfig.mlsProductId)
              ? 'during the billing period. '
              : 'before the subscription begins.'}
          </Text>
        </StyledSecondaryText>
      )}
      <StyledPrice data-cy="fee-description">
        <Text>{feeDescription}</Text>
        <Text>{formatCurrency(price)}</Text>
      </StyledPrice>
      <StyledTaxes data-cy="fee-taxes">
        <Text>+ Applicable Taxes</Text>
        <Text>{formatCurrency(taxes)}</Text>
      </StyledTaxes>
      <StyledTotal data-cy="fee-total">
        <Text strong>Total</Text>
        <Text strong>{formatCurrency(total)}</Text>
      </StyledTotal>
    </StyledTotalSection>
  );
};

const StyledTotalSection = styled(StyledActivateSection)`
  padding: 24px 0;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledSecondaryText = styled.div`
  padding: 10px 0;
`;
const StyledPrice = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  line-height: 22px;
  margin-top: 12px;
`;

const StyledTaxes = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  line-height: 22px;
  margin-top: 12px;
`;

const StyledTotal = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  line-height: 22px;
  margin-top: 12px;
`;

const StyledTitle = styled(Title)`
  && {
    margin: 0;
  }
`;

export default YourTotal;
